/**
 * API Service
 * Handles all API communication with backend
 */

import { CONFIG, getApiBaseUrl } from '../config/constants.js';
import { authService } from './authService.js';

class ApiService {
  constructor() {
    this.baseUrl = getApiBaseUrl();
  }

  /**
   * Get authorization headers
   */
  getHeaders() {
    const headers = {
      'Content-Type': 'application/json'
    };

    const token = authService.getAccessToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return headers;
  }

  // ---------------------------------------------------------------------------
  // Legacy REST — not used by taskpane.js or commands.js. The add-in downloads
  // raw data (GetTrialBalanceFlat, parties, master chart, currencies, etc.) and
  // builds P&L / BS / CF in Excel. Removed helpers were: fetchParties,
  // fetchGroupEntities, fetchTrialBalanceV2, fetchTrialBalance, fetchPnLReportV2,
  // fetchPnLReport, fetchTrialBalancesForCompanies, fetchPnLReportsForCompanies,
  // fetchPnLReportsForPeriods, fetchBalanceSheetV2, fetchBalanceSheet,
  // fetchBalanceSheetsForPeriods. Matching URLs are commented in constants.js;
  // restore implementations from git if you wire server-side reports again.
  // ---------------------------------------------------------------------------

  // ─── Raw Sync Endpoints ───────────────────────────────────────────────────

  /** Helper: POST JSON and return parsed response */
  async _post(endpoint, body) {
    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify(body)
    });
    if (!response.ok) {
      const text = await response.text().catch(() => response.statusText);
      throw new Error(`${endpoint} failed [${response.status}]: ${text}`);
    }
    return response.json();
  }

  /** Helper: GET and return parsed response */
  async _get(endpoint) {
    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      headers: this.getHeaders()
    });
    if (!response.ok) {
      const text = await response.text().catch(() => response.statusText);
      throw new Error(`${endpoint} failed [${response.status}]: ${text}`);
    }
    return response.json();
  }

  /**
   * Flat raw trial balance — one row per party × account × month.
   * No server-side currency conversion applied.
   */
  async fetchTrialBalanceFlat({ partyIds, groupEntityId, year, periodType = 12, period = 0, months = null, profileId = 1, includeCompanyElimination = false, includeHistoricalData = false, partyAccountGrain = true }) {
    return this._post(CONFIG.API.ENDPOINTS.TRIAL_BALANCE_FLAT, {
      partyIds: partyIds?.length > 0 ? partyIds : null,
      groupEntityId: groupEntityId ?? null,
      year,
      periodType,
      period,
      months,
      profileId,
      includeCompanyElimination,
      includeHistoricalData,
      partyAccountGrain
    });
  }

  /** All active ISO 4217 currencies. */
  async fetchCurrencies() {
    return this._get(CONFIG.API.ENDPOINTS.GET_CURRENCIES);
  }

  /** Consolidation group entities (not companies). Used for _Config Group entity dropdown. */
  async fetchGroupEntities() {
    return this._get(CONFIG.API.ENDPOINTS.GET_GROUP_ENTITIES);
  }

  /**
   * Raw exchange rates.
   * @param {object} [filter] - { fromCurrencyCodes, toCurrencyCodes, fromDate, toDate, limit }
   */
  async fetchExchangeRates(filter = {}) {
    return this._post(CONFIG.API.ENDPOINTS.GET_EXCHANGE_RATES, filter);
  }

  /**
   * Intercompany eliminations in a date range.
   * @param {number[]} partyIds - Empty = all accessible parties
   * @param {number} fromYear
   * @param {number} fromMonth - 1-12
   * @param {number} toYear
   * @param {number} toMonth - 1-12
   */
  async fetchIntercompanyEliminations(partyIds, fromYear, fromMonth, toYear, toMonth) {
    return this._post(CONFIG.API.ENDPOINTS.GET_INTERCOMPANY_ELIMINATIONS, {
      partyIds: partyIds?.length > 0 ? partyIds : null,
      fromYear,
      fromMonth,
      toYear,
      toMonth
    });
  }

  /** All party ownership records (group structure). */
  async fetchPartyOwnerships() {
    return this._get(CONFIG.API.ENDPOINTS.GET_PARTY_OWNERSHIPS);
  }

  /** Full party details for all accessible parties. */
  async fetchPartiesDetail() {
    return this._get(CONFIG.API.ENDPOINTS.GET_PARTIES_DETAIL);
  }

  /** All master chart of accounts profiles. */
  async fetchMasterProfiles() {
    return this._get(CONFIG.API.ENDPOINTS.GET_MASTER_PROFILES);
  }

  /** All master accounts (flat list across all profiles). */
  async fetchMasterAccounts() {
    return this._get(CONFIG.API.ENDPOINTS.GET_MASTER_ACCOUNTS);
  }

  /** All party-to-master account mappings for accessible parties. */
  async fetchAccountMappings() {
    return this._get(CONFIG.API.ENDPOINTS.GET_ACCOUNT_MAPPINGS);
  }

  /**
   * AI dashboard planner — sends user message + compact table metadata; returns assistant text + optional dashboard spec.
   * @param {{ userMessage: string, workbookTables: object[], workbookDiagnostics?: { rawSheetsWithoutExcelTable?: string[] }, conversationHistory?: { isUser: boolean, content: string }[] }} body
   */
  async requestExcelDashboardPlan(body) {
    return this._post(CONFIG.API.ENDPOINTS.EXCEL_ASSISTANT_PLAN, body);
  }
}

// Export singleton instance
export const apiService = new ApiService();
